import { useEffect } from 'react';
import { useConfigStore } from '@/store/useConfigStore';
import { SelectionCard } from '@/components/ui/SelectionCard';
import { StepperInput } from '@/components/ui/StepperInput';
import { Waves, GripVertical, Circle, AlignJustify, SplitSquareHorizontal, Square } from 'lucide-react';
import type { CurtainStyleConfig } from '@/types/curtain';

export function Step2CurtainStyle() {
  const { styleConfig, updateStyleConfig } = useConfigStore();

  useEffect(() => {
    if (styleConfig.isPair === undefined) {
      updateStyleConfig({ isPair: true });
    }
  }, [styleConfig.isPair, updateStyleConfig]);

  const handleUpdate = <K extends keyof CurtainStyleConfig>(key: K, value: CurtainStyleConfig[K]) => {
    updateStyleConfig({ [key]: value });
  };

  const headingStyle = styleConfig.headingStyle || 's-fold';
  const isPair = styleConfig.isPair !== false;

  const getFullnessRatio = (style: string) => {
    switch (style) {
      case 's-fold': return '2.2x - 2.5x';
      case 'pinch-pleat': return '2.5x';
      case 'eyelet': return '2.0x';
      case 'pencil-pleat': return '2.0x - 2.5x';
      default: return '2.0x';
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-300">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Curtain Style</h2>
          <p className="text-slate-600">Choose the heading style and panel configuration.</p>
        </div>
        <button
          onClick={() => useConfigStore.getState().setVisualizerOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-lg font-medium transition-colors border border-indigo-200"
        >
          <span>👓 View in 3D</span>
        </button>
      </div>

      <div className="space-y-4">
        <label className="text-sm font-medium text-slate-700 block">Heading Style</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SelectionCard
            title="S-Fold"
            description="Smooth continuous wave fold"
            icon={Waves}
            selected={headingStyle === 's-fold'}
            onClick={() => handleUpdate('headingStyle', 's-fold' as any)}
            extra={<span className="text-xs font-semibold bg-slate-100 px-2 py-1 rounded text-slate-600">~{getFullnessRatio('s-fold')}</span>}
          />
          <SelectionCard
            title="Pinch Pleat"
            description="Classic gathered triple pleat"
            icon={GripVertical}
            selected={headingStyle === 'pinch-pleat'}
            onClick={() => handleUpdate('headingStyle', 'pinch-pleat' as any)}
            extra={<span className="text-xs font-semibold bg-slate-100 px-2 py-1 rounded text-slate-600">~{getFullnessRatio('pinch-pleat')}</span>}
          />
          <SelectionCard
            title="Eyelet"
            description="Modern ring-threaded header"
            icon={Circle}
            selected={headingStyle === 'eyelet'}
            onClick={() => handleUpdate('headingStyle', 'eyelet' as any)}
            extra={<span className="text-xs font-semibold bg-slate-100 px-2 py-1 rounded text-slate-600">~{getFullnessRatio('eyelet')}</span>}
          />
          <SelectionCard
            title="Pencil Pleat"
            description="Tightly gathered pencil fold"
            icon={AlignJustify}
            selected={headingStyle === 'pencil-pleat'}
            onClick={() => handleUpdate('headingStyle', 'pencil-pleat' as any)}
            extra={<span className="text-xs font-semibold bg-slate-100 px-2 py-1 rounded text-slate-600">~{getFullnessRatio('pencil-pleat')}</span>}
          />
        </div>
      </div>

      <div className="space-y-4 pt-4 border-t border-slate-100">
        <label className="text-sm font-medium text-slate-700 block">Panel Layout</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SelectionCard
            title="Pair (Split)"
            description="Two panels opening from the center"
            icon={SplitSquareHorizontal}
            selected={isPair === true}
            onClick={() => handleUpdate('isPair', true)}
          />
          <SelectionCard
            title="Single Panel"
            description="One continuous panel"
            icon={Square}
            selected={isPair === false}
            onClick={() => handleUpdate('isPair', false)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-slate-100">
        <StepperInput
          label="Return Width"
          value={styleConfig.returnWidth || 80}
          onChange={(val) => handleUpdate('returnWidth', val)}
          unit="mm"
          min={0}
          max={300}
        />
        <StepperInput
          label="Overlap Width"
          value={styleConfig.overlap || 50}
          onChange={(val) => handleUpdate('overlap', val)}
          unit="mm"
          min={0}
          max={300}
        />
      </div>
    </div>
  );
}
